import { Component, inject, Input } from '@angular/core';

import { IonicModule, NavController, ToastController } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { ShowPerformance } from '../../models/showperformance.model';
import { RehearsalService } from '../../app/rehearsal/rehearsal.service';
import { usePrivilege } from '../../app/auth/privilege-hooks';
import { Privileges } from '../../models/privileges';

@Component({
    selector: 'app-rehearsal-url',
    templateUrl: './rehearsal-url.component.html',
    styleUrls: ['./rehearsal-url.component.scss'],
    imports: [IonicModule, TranslateModule]
})
export class RehearsalUrlComponent {

    @Input() public performance!: ShowPerformance;

    protected isGeneratingUrl = false;

    private readonly rehearsalService: RehearsalService = inject(RehearsalService);
    private readonly toastController: ToastController = inject(ToastController);
    private readonly navController: NavController = inject(NavController);

    /** Signal-reactive; re-evaluates when the user signal loads. */
    protected canEditRehearsal = usePrivilege(Privileges.RehearsalEdit);

    protected hasRehearsalExternalUrl(): boolean {
        return !!this.performance?.rehearsalExternalUrl;
    }

    protected async requestRehearsalExternalUrl(): Promise<void> {
        if (!this.performance?.id) {
            return;
        }
        try {
            this.isGeneratingUrl = true;
            const response = await this.rehearsalService.requestExternalRehearsalUrlForPerformance(this.performance.id);
            this.performance.rehearsalExternalUrl = response.token;
        } catch (e) {
            console.error('[RehearsalUrlComponent] Error requesting rehearsal URL:', e);
            const toast = await this.toastController.create({
                message: 'Kon geen link genereren. Probeer het opnieuw.',
                duration: 3000,
                color: 'danger'
            });
            await toast.present();
        } finally {
            this.isGeneratingUrl = false;
        }
    }

    protected getRehearsalExternalUrl(): string {
        if (!this.performance?.rehearsalExternalUrl) {
            return '';
        }
        const clientUrl = window.location.origin;
        return `${clientUrl}/rehearsal/external/${this.performance.rehearsalExternalUrl}`;
    }

    protected async copyRehearsalUrlToClipboard(): Promise<void> {
        const url = this.getRehearsalExternalUrl();
        await navigator.clipboard.writeText(url);
        const toast = await this.toastController.create({
            message: 'Link gekopieerd naar klembord',
            duration: 2000,
            color: 'success'
        });
        await toast.present();
    }

    protected sendRehearsalUrlViaWhatsapp(): void {
        const speeldatum: string = new Date(this.performance.datetime).toLocaleDateString('nl-NL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        const berichtHeader = '*Repetitie*';
        const berichtVooraf = `Hierbij de URL voor de repetities van ${this.performance.show?.name || 'CARO'} van ${speeldatum}:`;
        const rehearsalUrl = this.getRehearsalExternalUrl();
        const bericht = `${berichtHeader}\n${berichtVooraf}\n\n${rehearsalUrl}`;
        const gecodeerdBericht = encodeURIComponent(bericht);
        window.open(`https://wa.me/?text=${gecodeerdBericht}`, '_blank');
    }

    protected async navigateToFillInRehearsal(): Promise<void> {
        if (this.performance?.rehearsalExternalUrl) {
            await this.navController.navigateRoot(['/rehearsal', 'external', this.performance.rehearsalExternalUrl]);
        }
    }
}
